'use client';
import React, { useState, useEffect, useRef } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import Button from '@src/v3/components/coreUI/button';
import Slider from '@src/v3/components/coreUI/slider';
import Icon from '@src/v3/components/coreUI/icon';
import { Dialog, DialogContent } from '@src/v3/components/coreUI/dialog';
import { CropImageProps } from '@src/v3/interfaces/components/cropImage';
import { getCroppedImg } from '@src/v3/utils/helper';

const CropImage: React.FC<CropImageProps> = ({
  isOpen,
  file,
  imageSrc: propImageSrc,
  initialCropState,
  onClose,
  onSuccess,
  onUpdatePhoto,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [scale, setScale] = useState<number>(1);
  const [completedCrop, setCompletedCrop] = useState<Area | null>(null);

  useEffect(() => {
    if (propImageSrc) {
      // If imageSrc prop is provided, use it directly
      setImgSrc(propImageSrc);
      // Restore previous crop state if available
      if (initialCropState) {
        setCrop(initialCropState.crop);
        setScale(initialCropState.scale);
      } else {
        setCrop({ x: 0, y: 0 });
        setScale(1);
      }
    } else if (file) {
      // Otherwise, read from file
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc((reader.result as string) || '');
      });
      reader.readAsDataURL(file);
      // Reset for new file
      setCrop({ x: 0, y: 0 });
      setScale(1);
    } else {
      setImgSrc('');
      setCrop({ x: 0, y: 0 });
      setScale(1);
    }
    // Reset completed crop when image changes
    setCompletedCrop(null);
  }, [file, propImageSrc, initialCropState]);

  useEffect(() => {
    if (!imgSrc) return undefined;

    const timer = setTimeout(() => {
      setCrop((prev) => ({ x: prev.x + 0.01, y: prev.y }));
    }, 50);

    return () => clearTimeout(timer);
  }, [imgSrc, scale]);

  /**
   * Crop the image.
   *
   * @return {void}
   */
  const handleCropImage = async () => {
    if (!completedCrop || !imgSrc) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const croppedImage: any = await getCroppedImg(imgSrc, completedCrop, 0);

      const reader = new FileReader();
      reader.onloadend = () => {
        // Pass current crop state (scale and position) along with the cropped image
        const cropState = {
          scale,
          crop,
        };
        // Pass original image source so it can be stored for future edits
        onSuccess(reader.result as string, cropState, imgSrc);
        onClose();
      };
      reader.readAsDataURL(croppedImage);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  const handleUpdatePhoto = () => {
    if (onUpdatePhoto) {
      onUpdatePhoto();
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = event.target.files?.[0];
    if (newFile) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc((reader.result as string) || '');
      });
      reader.readAsDataURL(newFile);
      // Reset crop state when new file is selected
      setCrop({ x: 0, y: 0 });
      setScale(1);
      setCompletedCrop(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={true}
        className="!w-full md:!w-[496px] !max-w-full md:!max-w-[496px] bg-white rounded-none md:rounded-3xl shadow-[0px_317px_89px_0px_rgba(0,0,0,0.00),0px_203px_81px_0px_rgba(0,0,0,0.01),0px_114px_68px_0px_rgba(0,0,0,0.03),0px_51px_51px_0px_rgba(0,0,0,0.04),0px_13px_28px_0px_rgba(0,0,0,0.05)] flex flex-col justify-start items-start !p-0 h-full md:h-auto md:min-h-fit max-h-[100vh] md:max-h-none overflow-y-auto md:overflow-visible"
      >
        {/* Mobile Header */}
        <div className="md:hidden w-full px-5 pt-6 pb-4">
          <div className="self-stretch justify-start text-black font-geist text-2xl font-medium leading-9 tracking-[-0.48px]">
            Profile Photo
          </div>
        </div>

        {/* Desktop Content */}
        <div className="hidden md:flex w-full flex-col justify-start items-start">
          {/* Desktop Header - Section A */}
          <div className="self-stretch flex flex-col justify-start items-start gap-6 px-6 pt-8 pb-5">
            {/* Profile Photo Title */}
            <div className="self-stretch justify-start text-gray-1000 font-geist text-2xl font-medium leading-normal tracking-[-0.24px]">
              Profile Photo
            </div>

            {/* Cropper Area */}
            {!!imgSrc && (
              <div className="self-stretch flex justify-center items-center w-full h-[286px]">
                <div className="relative flex justify-center items-center rounded-lg overflow-hidden w-[448px] h-[286px] bg-black/[0.34]">
                  <Cropper
                    image={imgSrc}
                    crop={crop}
                    zoom={scale}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={(value) => setScale(value)}
                    onCropAreaChange={(_, croppedAreaPixels) => {
                      if (
                        croppedAreaPixels &&
                        croppedAreaPixels.width > 0 &&
                        croppedAreaPixels.height > 0
                      ) {
                        setCompletedCrop(croppedAreaPixels);
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Zoom Section */}
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              <div className="self-stretch justify-start text-black text-lg font-medium font-geist">
                Zoom
              </div>
              <Slider
                min={1}
                max={10}
                value={scale}
                variant="blue"
                size="md"
                sliderProps={{
                  step: 0.1,
                }}
                onChange={(value) => setScale(Number(value))}
              />
            </div>
          </div>

          {/* Footer Buttons - Desktop */}
          <div className="self-stretch flex items-center gap-5 px-6 pt-8 pb-5">
            <Button
              onClick={handleUpdatePhoto}
              variant="outline"
              className="flex-1 flex justify-center items-center gap-2.5 bg-gray-200 rounded-xl hover:bg-gray-200/80 border-0 h-12 px-5 py-2.5 min-w-0"
            >
              <Icon
                iconName="cameraPhoto"
                iconProps={{ className: 'w-5 h-5' }}
              />
              <span className="text-gray-1000/90 text-base font-semibold font-geist whitespace-nowrap">
                Update Photo
              </span>
            </Button>
            <Button
              onClick={handleCropImage}
              variant="primary"
              className="flex-1 flex justify-center items-center gap-2.5 bg-focus rounded-xl hover:bg-focus/90 border-0 h-12 px-5 py-2.5 min-w-0"
              disabled={!completedCrop}
            >
              <span className="text-white text-base font-semibold font-geist whitespace-nowrap">
                Confirm
              </span>
            </Button>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="md:hidden w-full flex flex-col flex-1 min-h-0 overflow-y-auto">
          {/* Cropper Area - Mobile */}
          {!!imgSrc && (
            <div className="self-stretch flex justify-center items-center w-full flex-1 min-h-0 px-5 pb-4">
              <div className="relative flex justify-center items-center rounded-lg overflow-hidden w-full max-w-full h-full min-h-[300px] bg-gray-200">
                <Cropper
                  image={imgSrc}
                  crop={crop}
                  zoom={scale}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onCropComplete={(
                    _croppedArea: Area,
                    croppedAreaPixels: Area
                  ) => setCompletedCrop(croppedAreaPixels)}
                  onZoomChange={(value) => setScale(value)}
                />
              </div>
            </div>
          )}

          {/* Zoom Section - Mobile */}
          <div className="self-stretch flex flex-col justify-start items-start gap-4 px-5 pb-6">
            <div className="self-stretch justify-start text-black text-base font-medium font-geist leading-6">
              Zoom
            </div>
            <Slider
              min={1}
              max={10}
              value={scale}
              variant="blue"
              size="md"
              sliderProps={{
                step: 0.1,
              }}
              onChange={(value) => setScale(Number(value))}
            />
          </div>
        </div>

        {/* Footer Buttons - Mobile */}
        <div className="md:hidden self-stretch flex items-center gap-4 px-5 pb-6">
          <Button
            onClick={handleUpdatePhoto}
            variant="outline"
            className="flex-1 flex justify-center items-center gap-2.5 bg-background-200 rounded-xl hover:bg-background-200/80 border-0 h-12 px-5 py-2.5 min-w-0"
          >
            <Icon iconName="cameraPhoto" iconProps={{ className: 'w-5 h-5' }} />
            <span className="text-gray-1000 text-base font-semibold font-geist whitespace-nowrap">
              Update Photo
            </span>
          </Button>
          <Button
            onClick={handleCropImage}
            variant="primary"
            className="flex-1 flex justify-center items-center gap-2.5 bg-focus rounded-xl hover:bg-focus/90 border-0 h-12 px-5 py-2.5 min-w-0"
            disabled={!completedCrop}
          >
            <span className="text-white text-base font-semibold font-geist whitespace-nowrap">
              Confirm
            </span>
          </Button>
        </div>

        {/* Hidden file input for Update Photo */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CropImage;
